import styled from 'styled-components';
import { Zap, Shield, Clock, CreditCard, ArrowRight, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const HydrixPayContainer = styled.div`
    width: 100%;
    color: white;
    font-family: 'Very Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    position: relative;
    padding-top: 6rem;
    
    @media screen and (max-width: 768px) {
        padding: 3rem 0;
    }
`;

const HydrixPayWrapper = styled.div`
    width: 80%;
    margin: 0 auto;
    
    @media screen and (max-width: 768px) {
        width: 90%;
    }
`;

const HeaderSection = styled.div`
    text-align: center;
    margin-bottom: 4rem;
    
    @media screen and (max-width: 768px) {
        margin-bottom: 2rem;
        text-align: left;
    }
`;

const Badge = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 20px;
    padding: 0.5rem 1rem;
    color: #3B82F6;
    font-family: 'Very Sans Bold', sans-serif;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 1.5rem;
    
    @media screen and (max-width: 768px) {
        font-size: 12px;
        padding: 0.4rem 0.8rem;
    }
`;

const MainTitle = styled.h2`
    font-family: 'Very Sans Headline', sans-serif;
    font-size: 48px;
    font-weight: 600;
    color: #ffffff;
    margin: 0 0 1.5rem 0;
    line-height: 1.2;
    
    .highlight {
        background: linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }
    
    @media screen and (max-width: 768px) {
        font-size: 46px;
        margin-bottom: 1rem;
        tex-align: left;
    }
`;

const Subtitle = styled.p`
    color: #94A3B8;
    font-family: 'Very Sans', sans-serif;
    font-size: 18px;
    max-width: 600px;
    margin: 0 auto 2rem auto;
    line-height: 1.6;
    
    @media screen and (max-width: 768px) {
        font-size: 16px;
        margin-bottom: 1.5rem;
    }
`;

const CTAButtons = styled.div`
    display: flex;
    gap: 1rem;
    justify-content: center;
    align-items: center;
    
    @media screen and (max-width: 768px) {
        flex-direction: column;
        width: 100%;
    }
`;

const PrimaryButton = styled.button`
    background: #3B82F6;
    color: white;
    border: none;
    padding: 14px 32px;
    border-radius: 8px;
    font-family: 'Very Sans Bold', sans-serif;
    font-weight: 600;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    &:hover {
        background: #2563EB;
        transform: translateY(-2px);
        box-shadow: 0 8px 25px rgba(59, 130, 246, 0.3);
    }
    
    @media screen and (max-width: 768px) {
        width: 100%;
        justify-content: center;
        font-size: 14px;
        padding: 12px 24px;
    }
`;

const SecondaryButton = styled.button`
    background: transparent;
    color: #94A3B8;
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 14px 32px;
    border-radius: 8px;
    font-family: 'Very Sans Bold', sans-serif;
    font-weight: 600;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
        background: rgba(255, 255, 255, 0.05);
        color: #ffffff;
        border-color: rgba(255, 255, 255, 0.2);
    }
    
    @media screen and (max-width: 768px) {
        width: 100%;
        font-size: 14px;
        padding: 12px 24px;
    }
`;

const FeaturesGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    margin: 4rem 0;
    
    @media screen and (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1rem;
        margin: 2rem 0;
    }
`;

const FeatureCard = styled.div`
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 2rem;
    transition: all 0.3s ease;
    
    &:hover {
        background: rgba(255, 255, 255, 0.05);
        border-color: rgba(59, 130, 246, 0.5);
        transform: translateY(-5px);
        box-shadow: 0 12px 40px rgba(59, 130, 246, 0.2);
    }
    
    @media screen and (max-width: 768px) {
        padding: 1.5rem;
    }
`;

const IconWrapper = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 12px;
    background: rgba(59, 130, 246, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;
    
    @media screen and (max-width: 768px) {
        width: 48px;
        height: 48px;
        margin-bottom: 1rem;
    }
`;

const FeatureTitle = styled.h3`
    font-family: 'Very Sans Bold', sans-serif;
    font-size: 20px;
    font-weight: 600;
    color: #ffffff;
    margin: 0 0 0.75rem 0;
    
    @media screen and (max-width: 768px) {
        font-size: 18px;
    }
`;

const FeatureDescription = styled.p`
    color: #94A3B8;
    font-family: 'Very Sans Light', sans-serif;
    font-size: 15px;
    margin: 0;
    line-height: 1.6;
    
    @media screen and (max-width: 768px) {
        font-size: 14px;
    }
`;

const UseCaseSection = styled.div`
    margin: 6rem 0;
    
    @media screen and (max-width: 768px) {
        margin: 3rem 0;
    }
`;

const SectionTitle = styled.h3`
    font-family: 'Very Sans Bold', sans-serif;
    font-size: 36px;
    font-weight: 600;
    color: #ffffff;
    text-align: center;
    margin: 0 0 3rem 0;
    
    @media screen and (max-width: 768px) {
        font-size: 28px;
        margin-bottom: 2rem;
    }
`;

const UseCaseGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
    
    @media screen and (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
`;

const UseCaseCard = styled.div`
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    padding: 2rem;
    
    @media screen and (max-width: 768px) {
        padding: 1.5rem;
    }
`;

const UseCaseTitle = styled.h4`
    font-family: 'Very Sans Bold', sans-serif;
    font-size: 20px;
    font-weight: 600;
    color: #ffffff;
    margin: 0 0 1rem 0;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    
    @media screen and (max-width: 768px) {
        font-size: 18px;
    }
`;

const UseCaseList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const UseCaseItem = styled.li`
    color: #94A3B8;
    font-family: 'Very Sans Light', sans-serif;
    font-size: 15px;
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    line-height: 1.5;
    
    &::before {
        content: '';
        min-width: 6px;
        min-height: 6px;
        width: 6px;
        height: 6px;
        background: #3B82F6;
        border-radius: 50%;
        margin-top: 0.5rem;
    }
    
    @media screen and (max-width: 768px) {
        font-size: 14px;
    }
`;

const StatsSection = styled.div`
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    padding: 3rem;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2rem;
    
    @media screen and (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1.5rem;
        padding: 2rem 1.5rem;
    }
`;

const StatItem = styled.div`
    text-align: center;
    border-right: 1px solid rgba(255, 255, 255, 0.1);
    
    &:last-child {
        border-right: none;
    }
    
    @media screen and (max-width: 768px) {
        border-right: none;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        padding-bottom: 1.5rem;
        
        &:last-child {
            border-bottom: none;
            padding-bottom: 0;
        }
    }
`;

const StatNumber = styled.div`
    font-family: 'Very Sans Bold', sans-serif;
    font-size: 48px;
    font-weight: 600;
    color: #3B82F6;
    margin-bottom: 0.5rem;
    
    @media screen and (max-width: 768px) {
        font-size: 36px;
    }
`;

const StatLabel = styled.div`
    font-family: 'Very Sans Light', sans-serif;
    font-size: 16px;
    color: #94A3B8;
    
    @media screen and (max-width: 768px) {
        font-size: 14px;
    }
`;

const GradientOrb = styled.div`
    position: absolute;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%);
    pointer-events: none;
    
    &.top-right {
        top: -10%;
        right: -10%;
    }
    
    &.bottom-left {
        bottom: -10%;
        left: -10%;
        background: radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%);
    }
    
    @media screen and (max-width: 768px) {
        width: 400px;
        height: 400px;
    }
`;

const HydrixPay = () => {

    const navigate = useNavigate();

    const features = [
        {
            icon: <Zap size={28} color="#3B82F6" />,
            title: 'Recurring Payments',
            description: 'Set up automated subscription payments directly from your non-custodial wallet. Sign once, pay forever.'
        },
        {
            icon: <CreditCard size={28} color="#3B82F6" />,
            title: 'One-Time Payments',
            description: 'Make instant, secure payments for goods and services without intermediaries or custody risks.'
        },
        {
            icon: <Shield size={28} color="#3B82F6" />,
            title: 'Non-Custodial',
            description: 'You maintain full control of your funds. No third party ever holds your assets.'
        },
        {
            icon: <Clock size={28} color="#3B82F6" />,
            title: 'Instant Settlement',
            description: 'Payments settle immediately on-chain with full transparency and cryptographic verification.'
        }
    ];

    const handleGoToDocs = () => {
        window.open(
            'https://ornate-gecko-119471.netlify.app',
            '_blank',
            'noopener, noreferrer'
        );
    };

    const handleNavigateToPay = () => {
        navigate("/virtualcard");
    };

    return (
        <HydrixPayContainer>
            <GradientOrb className="top-right" />
            <GradientOrb className="bottom-left" />
            
            <HydrixPayWrapper>
                <HeaderSection>
                    
                    
                    <MainTitle>
                        Hydrix<span className="highlight">Pay</span>
                    </MainTitle>
                    
                    <Subtitle>
                        Enable recurring and one-off payments directly from your wallet. 
                        No custody, no intermediaries, complete control.
                    </Subtitle>
                    
                    <CTAButtons>
                        <PrimaryButton onClick={handleNavigateToPay}>
                            Get Started
                            <ArrowRight size={18} />
                        </PrimaryButton>
                        <SecondaryButton onClick={handleGoToDocs}>
                            View Docs
                        </SecondaryButton>
                    </CTAButtons>
                </HeaderSection>

                <FeaturesGrid>
                    {features.map((feature, index) => (
                        <FeatureCard key={index}>
                            <IconWrapper>
                                {feature.icon}
                            </IconWrapper>
                            <FeatureTitle>{feature.title}</FeatureTitle>
                            <FeatureDescription>{feature.description}</FeatureDescription>
                        </FeatureCard>
                    ))}
                </FeaturesGrid>

                <UseCaseSection>
                    <UseCaseGrid>
                        <UseCaseCard>
                            <UseCaseTitle>
                                <CheckCircle size={24} color="#3B82F6" />
                                Recurring Subscriptions
                            </UseCaseTitle>
                            <UseCaseList>
                                <UseCaseItem>Streaming services (Netflix, Spotify)</UseCaseItem>
                                <UseCaseItem>SaaS platforms and software licenses</UseCaseItem>
                                <UseCaseItem>Membership fees and gym subscriptions</UseCaseItem>
                                <UseCaseItem>Domain registrations and hosting</UseCaseItem>
                            </UseCaseList>
                        </UseCaseCard>
                        
                        <UseCaseCard>
                            <UseCaseTitle>
                                <CheckCircle size={24} color="#3B82F6" />
                                One-Time Payments
                            </UseCaseTitle>
                            <UseCaseList>
                                <UseCaseItem>E-commerce purchases</UseCaseItem>
                                <UseCaseItem>Service provider payments</UseCaseItem>
                                <UseCaseItem>Digital content and NFTs</UseCaseItem>
                                <UseCaseItem>Peer-to-peer transactions</UseCaseItem>
                            </UseCaseList>
                        </UseCaseCard>
                    </UseCaseGrid>
                </UseCaseSection>

               
            </HydrixPayWrapper>
        </HydrixPayContainer>
    );
};

export default HydrixPay;